import React, { useEffect, useState } from "react";
import { gql, useApolloClient } from "@apollo/client";
import { useNavigate } from "react-router-dom";

/** Same source as QuestionListing — reliable counts per category. */
const CATEGORY_STATS_VIA_GET_ALL = gql`
  query CategoryStatsGetAllQuestion(
    $courseId: String!
    $page: Int
    $limit: Int
    $categoryId: String!
  ) {
    getAllQuestion(
      courseId: $courseId
      page: $page
      limit: $limit
      categoryId: $categoryId
    ) {
      totalCount
      questions {
        id
        difficulty
      }
    }
  }
`;

const PAGE_SIZE = 200;

const DIFFICULTY_STATS = [
  { key: "easy", label: "Easy", valueKey: "easy", textClass: "text-emerald-700" },
  { key: "medium", label: "Med", valueKey: "medium", textClass: "text-amber-700" },
  { key: "hard", label: "Hard", valueKey: "hard", textClass: "text-rose-700" },
];

function tallyDifficulty(rows) {
  let easy = 0;
  let medium = 0;
  let hard = 0;
  for (const q of rows) {
    const d = String(q?.difficulty ?? "").toUpperCase().trim();
    if (d === "EASY") easy += 1;
    else if (d === "MEDIUM") medium += 1;
    else if (d === "HARD") hard += 1;
  }
  return { easy, medium, hard };
}

function DifficultyStat({ label, value, textClass, loading }) {
  if (loading) {
    return <span className="inline-block h-4 w-12 animate-pulse rounded bg-slate-100" />;
  }
  return (
    <span className={`text-xs font-semibold tabular-nums ${textClass}`}>
      <span className="uppercase tracking-wide">{label}</span>{" "}
      <span>{value}</span>
    </span>
  );
}

const CategoryCard = ({ name, cat_id, course_id }) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    easy: 0,
    medium: 0,
    hard: 0,
  });

  useEffect(() => {
    if (!cat_id || !course_id) {
      setStatsLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setStatsLoading(true);
      let page = 1;
      let totalCount = 0;
      const collected = [];
      try {
        while (!cancelled) {
          const { data, errors } = await client.query({
            query: CATEGORY_STATS_VIA_GET_ALL,
            variables: {
              courseId: String(course_id),
              categoryId: String(cat_id),
              page,
              limit: PAGE_SIZE,
            },
            fetchPolicy: "network-only",
            errorPolicy: "all",
          });

          if (cancelled) return;
          if (errors?.length) {
            console.warn("CategoryCard stats:", errors);
          }

          const block = data?.getAllQuestion;
          if (!block) break;

          totalCount =
            typeof block.totalCount === "number"
              ? block.totalCount
              : parseInt(String(block.totalCount), 10) || 0;
          collected.push(...(block.questions ?? []));

          const batchLen = block.questions?.length ?? 0;
          const fetched = collected.length;
          if (fetched >= totalCount || batchLen === 0) {
            break;
          }
          page += 1;
        }

        if (cancelled) return;

        const { easy, medium, hard } = tallyDifficulty(collected);
        setStats({
          total: totalCount || collected.length,
          easy,
          medium,
          hard,
        });
      } catch (err) {
        console.error("CategoryCard stats fetch failed:", err);
        if (!cancelled) setStats({ total: 0, easy: 0, medium: 0, hard: 0 });
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cat_id, course_id, client]);

  const handleClick = () => {
    navigate(`/admin/courses/${course_id}/${cat_id}`);
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className="group relative flex h-full min-h-[148px] w-full cursor-pointer flex-col rounded-xl border border-slate-200/90 bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-all duration-200 hover:border-[#2d5670]/35 hover:shadow-[0_10px_28px_rgba(15,23,42,0.08)] sm:p-6"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <header className="flex items-start justify-between gap-4">
        <h3 className="min-w-0 flex-1 truncate text-[17px] font-semibold leading-snug text-slate-900 transition-colors group-hover:text-[#2d5670]">
          {name}
        </h3>
        <div className="shrink-0 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
            Total Questions
          </p>
          {statsLoading ? (
            <div className="mx-auto mt-4 h-9 w-12 animate-pulse rounded bg-slate-100" />
          ) : (
            <p className="mt-4 text-3xl font-bold tabular-nums leading-none text-slate-900">
              {stats.total}
            </p>
          )}
        </div>
      </header>

      <div className="mt-auto pt-6 sm:pt-7">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          {DIFFICULTY_STATS.map(({ key, label, valueKey, textClass }) => (
            <DifficultyStat
              key={key}
              label={label}
              value={stats[valueKey]}
              textClass={textClass}
              loading={statsLoading}
            />
          ))}
        </div>
      </div>
    </article>
  );
};

export default CategoryCard;
